// Mock data for The Soul Script website

export const siteConfig = {
  name: "The Soul Script",
  tagline: "A reflective sanctuary for seekers",
  description: "Where inquiry, ritual, and emotional depth shape conscious living.",
  email: "ayesha.thesoulscript@gmail.com",
  calendlyUrl: "https://calendly.com/thesoulscript",
};

export const heroSlides = [
  {
    id: 0,
    type: "brand",
    title: "The Soul Script",
    subtitle: "A Reflective Sanctuary for Seekers",
    description: "Where inquiry, ritual, and emotional depth shape conscious living.",
    image: "https://images.unsplash.com/photo-1765891597474-e290eabcb626?w=1920&q=80",
    cta: { text: "Enter the Sanctuary", link: "#philosophy" },
  },
  {
    id: 1,
    type: "service",
    title: "Coaching as Inquiry",
    subtitle: "Not Instruction",
    description: "A space to ask better questions and build a steadier present.",
    image: "https://images.unsplash.com/photo-1604785800517-e972a4c16f2b?w=1920&q=80",
    cta: { text: "Work With Me", link: "/coaching" },
  },
  {
    id: 2,
    type: "service",
    title: "Tarot as Reflection",
    subtitle: "Not Prediction",
    description: "A mirror for understanding, a practice that brings steadiness where there was once only searching.",
    image: "https://images.unsplash.com/photo-1600429991827-5224817554f8?w=1920&q=80",
    cta: { text: "Book a Reading", link: "/tarot" },
  },
  {
    id: 3,
    type: "service",
    title: "The Collective",
    subtitle: "Monthly Guidance",
    description: "Reflective readings for the collective journey.",
    image: "https://images.unsplash.com/photo-1601128688653-7dc405e3ac4d?w=1920&q=80",
    cta: { text: "Read the Collective", link: "/collective" },
  },
];

export const services = [
  {
    id: "coaching",
    title: "Work With Me",
    subtitle: "Coaching",
    description: "Structured, emotionally intelligent sessions rooted in inquiry—not instruction.",
    link: "/coaching",
    image: "https://images.unsplash.com/photo-1712252864284-89806311d85b?w=800&q=80",
    color: "bottle-green",
  },
  {
    id: "tarot",
    title: "Book a Reading",
    subtitle: "Tarot",
    description: "Insight and reflection—not prediction or fortune.",
    link: "/tarot",
    image: "https://images.unsplash.com/photo-1623230951203-1f8fa5298426?w=800&q=80",
    color: "deep-red",
  },
  {
    id: "collective",
    title: "The Collective",
    subtitle: "Monthly Guidance",
    description: "Reflective readings for the collective journey.",
    link: "/collective",
    image: "https://images.unsplash.com/photo-1548097341-3d7b4821f984?w=800&q=80",
    color: "midnight-blue",
  },
];

export const tarotReadings = [
  {
    id: 1,
    name: "Clarity Session",
    duration: "45 minutes",
    price: 22,
    currency: "€",
    description: "A focused session designed for specific questions, emotional fog, or moments of confusion. Supports clarity and grounded perspective without overwhelm.",
    includes: [
      "45-minute live session via video",
      "Grounded, ethical tarot reading",
      "Reflective dialogue",
      "Session recording"
    ],
    calendlyEvent: "clarity-tarot-session",
  },
  {
    id: 2,
    name: "Guidance Session",
    duration: "60 minutes",
    price: 34,
    currency: "€",
    description: "A deeper exploration of recurring patterns, relationships, or transitions. Allows time for reflection, dialogue, and integration prompts after the session.",
    includes: [
      "60-minute live session via video",
      "Grounded, ethical tarot reading",
      "Reflective dialogue",
      "Session recording",
      "Reflection prompts",
      "Integration notes"
    ],
    calendlyEvent: "guidance-tarot-session",
  },
  {
    id: 3,
    name: "Integration Session",
    duration: "75 minutes",
    price: 47,
    currency: "€",
    description: "A premium reflective session for deep self-inquiry and synthesis. Ideal for major transitions, post-coaching integration, or complex inner processes.",
    includes: [
      "75-minute live session via video",
      "Grounded, ethical tarot reading",
      "Reflective dialogue",
      "Session recording",
      "Detailed reflection prompts",
      "Comprehensive integration notes",
      "Follow-up email support"
    ],
    calendlyEvent: "integration-tarot-session",
  },
];

export const tarotPageCopy = {
  opening: {
    headline: "Insight, Not Prediction",
    subheadline: "Tarot as Reflection",
    intro: "Tarot here is not about fortune-telling or certainty. It is a reflective practice—a way of seeing more clearly what already is.",
    description: "The cards serve as symbolic mirrors, illuminating inner patterns, emotional landscapes, and the places where conscious choice remains. This work supports emotional resilience, self-acceptance, and the courage to meet yourself honestly. There are no guarantees, no predictions—only steadiness, care, and inquiry."
  },
  philosophy: {
    headline: "Reading Philosophy",
    points: [
      {
        title: "Not Prediction",
        description: "Tarot is not used to predict outcomes or guarantee results. The future is not fixed—it is shaped by your choices, your awareness, and your willingness to engage with what is."
      },
      {
        title: "Symbolic Mirrors",
        description: "The cards function as symbolic mirrors for insight and reflection. They illuminate patterns, possibilities, and perspectives—not certainties or mandates."
      },
      {
        title: "Clarity, Not Dependency",
        description: "The intention is clarity, containment, and self-trust. This practice supports your own inner knowing—it does not replace it or create dependency."
      }
    ]
  },
  whatYouReceive: {
    headline: "What You Receive",
    items: [
      "Grounded, ethical tarot reading",
      "Reflective dialogue throughout the session",
      "Session recording (where applicable)",
      "Reflection prompts or integration notes (Guidance and Integration sessions)"
    ]
  },
  ethicalNote: {
    headline: "A Note on What Tarot Is—And Isn't",
    content: "Tarot is a reflective practice for self-awareness and conscious choice. It is not a substitute for medical, legal, or mental health care. There are no certainty claims, no fear-based messaging, and no promises of specific outcomes. This work supports your capacity to meet yourself and your life with greater clarity and presence—nothing more, nothing less."
  }
};

export const coachingPackages = [
  {
    id: 1,
    name: "Foundation",
    sessions: "4 Sessions",
    price: 128,
    pricePerSession: 32,
    currency: "€",
    description: "A grounded starting point for those ready to begin the journey of self-inquiry. We'll establish your core questions and build practices for emotional resilience.",
    includes: [
      "4 × 60-minute sessions",
      "Session recordings",
      "Reflection exercises",
      "Email support between sessions"
    ],
    calendlyEvent: "foundation-coaching",
  },
  {
    id: 2,
    name: "Deepening",
    sessions: "8 Sessions",
    price: 238,
    pricePerSession: 30,
    currency: "€",
    description: "For those committed to sustained inquiry and meaningful inner change. We'll work with recurring patterns and build sustainable practices for a steadier inner life.",
    includes: [
      "8 × 60-minute sessions",
      "Session recordings",
      "Customized practices",
      "Priority email support",
      "1 integration tarot session"
    ],
    calendlyEvent: "deepening-coaching",
  },
  {
    id: 3,
    name: "Immersion",
    sessions: "12 Sessions",
    price: 340,
    pricePerSession: 29,
    currency: "€",
    description: "A quarter of devoted inner work for those seeking deep transformation. This container offers the time, space, and support for true integration.",
    includes: [
      "12 × 60-minute sessions",
      "Session recordings",
      "Bespoke ritual practices",
      "Unlimited email support",
      "2 integration tarot sessions",
      "Quarterly review session"
    ],
    calendlyEvent: "immersion-coaching",
  },
];

export const coachingPageCopy = {
  opening: {
    headline: "Coaching as Inquiry",
    subheadline: "Not Instruction",
    intro: "This is a space for reflective practice—where thoughtful questioning replaces advice-giving, and conscious living emerges through honest self-examination.",
    description: "Coaching here is not about fixing what's broken or achieving external goals. It's about building emotional resilience, practising self-acceptance, and finding the courage to meet yourself with honesty. Every session is designed to help you cultivate a steadier inner life—one grounded in awareness, not anxiety."
  },
  whoIsThisFor: {
    headline: "Who This Is For",
    description: "This work is for spiritually curious, intellectually grounded, emotionally aware seekers who want depth, not drama.",
    traits: [
      "You're tired of surface-level advice and ready for genuine inquiry",
      "You value reflection over reaction, depth over quick fixes",
      "You're willing to sit with discomfort in service of growth",
      "You want a space for honest self-examination, not validation",
      "You seek resilience and self-acceptance, not perfection"
    ],
    notFor: [
      "This is not therapy or a substitute for mental health treatment",
      "This is not predictive or prescriptive",
      "This is not performative spirituality or influencer energy",
      "This is not a space for quick fixes or motivational slogans"
    ]
  },
  howSessionsWork: {
    headline: "How Sessions Work",
    description: "Each session is a held space for inquiry—structured yet responsive to what emerges.",
    elements: [
      {
        title: "60-Minute One-on-One Sessions",
        description: "Dedicated time for focused, uninterrupted conversation via video call."
      },
      {
        title: "Quiet, Focused Conversations",
        description: "No agenda-pushing. We follow what's alive for you in the moment."
      },
      {
        title: "Thoughtful Questioning",
        description: "Questions designed to illuminate patterns, not impose answers."
      },
      {
        title: "Space for Reflection",
        description: "Room to process, pause, and sit with what arises without rushing."
      },
      {
        title: "Clear Boundaries & Confidentiality",
        description: "A safe container built on respect, privacy, and professional ethics."
      }
    ]
  }
};

export const collectiveReadings = [
  {
    id: 4,
    month: "June 2026",
    title: "When Patterns Become Visible",
    excerpt: "June draws attention to what binds you. Habits, attachments, and patterns that operate without much thought. This is not about judgment. It is about recognition.",
    image: "https://images.unsplash.com/photo-1560563609-3b4b1f5c2122?w=1200&q=80",
    slug: "june-2026",
  },
  {
    id: 3,
    month: "May 2026",
    title: "When Clarity Is Not Immediate",
    excerpt: "May does not offer immediate clarity. It slows things down. Where March asked for action and April asked for truth, May asks you to sit with what is still unresolved.",
    image: "https://images.unsplash.com/photo-1761755227421-b5e07efec0be?w=1200&q=80",
    slug: "may-2026",
  },
  {
    id: 2,
    month: "April 2026",
    title: "Truth, Choice, and Inner Alignment",
    excerpt: "April moves differently. There is less urgency, but more clarity. A quieter current, but one that asks for precision.",
    image: "https://images.unsplash.com/photo-1665518337917-97ab2ce2b793?w=1200&q=80",
    slug: "april-2026",
  },
  {
    id: 1,
    month: "March 2026",
    title: "Stepping Into Personal Authority",
    excerpt: "March carries a shift. Where recent months may have asked for reflection, this one asks for movement — not hurried, not reactive, but grounded and deliberate.",
    image: "https://images.unsplash.com/photo-1757265500084-e3a97ddf007e?w=1200&q=80",
    slug: "march-2026",
  },
];

export const toolkitProducts = [
  {
    id: 1,
    name: "The Daily Inquiry Journal",
    price: 24,
    currency: "€",
    description: "A 90-day guided journal for cultivating the practice of self-inquiry. Each day offers a question, a reflection prompt, and space for honest exploration.",
    image: "https://images.unsplash.com/photo-1601128688653-7dc405e3ac4d?w=800&q=80",
    status: "coming-soon",
  },
];

export const mainToolkit = {
  name: "The Soul Script – Reflective Practice Toolkit",
  tagline: "A companion for those who want to sit with themselves more honestly",
  price: 16,
  currency: "€",
  description: "A grounded, self-guided resource for seekers who value depth, clarity, and personal responsibility in their inner work. This toolkit supports emotional resilience, self-acceptance, and the courage to meet yourself with honesty—without dependency on external guidance.",
  image: "https://images.unsplash.com/photo-1601128688653-7dc405e3ac4d?w=800&q=80",
  purchaseUrl: "#", // Replace with actual Gumroad/Stripe link
  whatsInside: [
    {
      title: "Guided Reflection Prompts",
      description: "Thoughtfully crafted questions designed to illuminate patterns, beliefs, and the places where choice remains."
    },
    {
      title: "Journaling Exercises",
      description: "Structured yet spacious exercises for honest self-examination—without pressure or performance."
    },
    {
      title: "Grounding Practices",
      description: "Simple, embodied practices for moments of overwhelm, uncertainty, or emotional intensity."
    },
    {
      title: "Gentle Rituals for Everyday Life",
      description: "Small, intentional acts that anchor you in presence and support conscious living."
    },
    {
      title: "Frameworks for Self-Inquiry",
      description: "Clear structures for exploring recurring themes, decisions, and transitions with greater awareness."
    }
  ],
  whoIsThisFor: [
    "You want tools to practise reflection independently",
    "You value depth over quick fixes",
    "You're ready to sit with yourself more honestly",
    "You seek clarity through inquiry, not answers",
    "You appreciate structure that doesn't constrain"
  ],
  whatThisIsNot: [
    "This is not predictive—it won't tell you what will happen",
    "This is not prescriptive—it won't tell you what to do",
    "This is not performative—it's for genuine inner work, not display",
    "This is not therapy—it does not replace professional mental health support"
  ],
  delivery: {
    headline: "When It's Ready",
    description: "Once launched, you'll receive immediate access to download the complete toolkit. A welcome email will guide you on how to begin—gently, at your own pace.",
    steps: [
      "Get notified the moment the toolkit launches",
      "Complete your purchase through our secure payment link",
      "Receive instant access to download the toolkit",
      "Begin with the welcome guide at your own rhythm"
    ]
  }
};

export const clarityResetToolkit = {
  name: "The Clarity Reset Toolkit",
  tagline: "A comprehensive digital sanctuary for recalibration",
  description: "When life feels overwhelming and clarity seems distant, this toolkit offers a structured path back to center. Designed for moments of transition, confusion, or simply the need to reset.",
  features: [
    "7-day guided reset journey",
    "Audio meditations for each phase",
    "Reflective journaling prompts",
    "Tarot spreads for self-inquiry",
    "Integration rituals and practices",
    "Lifetime access and updates",
  ],
  status: "coming-soon",
  image: "https://images.unsplash.com/photo-1663872585330-437b683914c2?w=800&q=80",
};

export const testimonials = [
  {
    id: 1,
    quote: "Few coaches can combine the esoteric with grounded reality the way Ayesha can. As a skeptic, it was difficult for me to believe in tarot and readings until I experienced them with Ayesha. Her readings are not prescriptive, not merely parlor tricks. She used tarot as one of many tools to help me understand myself and my thought patterns and help me work on them. While I continue to work with a therapist, Ayesha has helped me set a course for my future.",
    author: "Aadhar M.",
    context: "Tarot Client",
  },
  {
    id: 2,
    quote: "What can I say about Ayesha, I call her my soul sister. We connected incidentally over social media and my admiration for her talent and captivating poetry. Over the years she has been there for me on many occasions and I truly appreciate her ability to share stillness and guidance from a place of peace. I have recommended her to friends who feel the same way and I'm truly grateful to have her in my life.",
    author: "Rebecca A.",
    context: "Coaching Client",
  },
];

// Homepage exact copy from brand brief
export const homePageCopy = {
  opening: `A reflective sanctuary for seekers—where inquiry, ritual, and emotional depth shape conscious living.`,
  
  philosophy: `The Soul Script is a space born from lived experience. From navigating questions of identity and independence, witnessing mental health struggles up close, surviving illness, and learning—often the hard way—what it means to build an inner life that can truly hold you.

Tarot entered this journey not as a spectacle, but as a refuge. A language for understanding, a mirror for reflection, and a practice that brought steadiness where there was once only searching. Over time, it became clear that this path was not only personal—it was a calling to serve.

Here, coaching and tarot come together as practices of inquiry. Not to predict your future, but to help you build a steadier present. Not to offer answers, but to help you ask better questions. Every offering is designed to support emotional containment, emotional resilience, and a deeper relationship with the self—so that self-acceptance becomes possible, and courage replaces self-doubt.

This is a space for those who wish to cultivate the courage and curiosity to know themselves more honestly. To meet their inner life with steadiness rather than fear. To live deliberately, with awareness and compassion.`,
  
  closing: `This is not a place for quick fixes or performative spirituality.
It is a space for those who take their inner work seriously—and wish to walk that path with depth, honesty, and care.`,
};

// About/Philosophy page exact copy from brand brief
export const aboutPageCopy = {
  opening: `The Soul Script was not created from theory. It was born from necessity.`,
  
  journey: `Growing up around emotional struggle, learning independence early, and later facing illness changed the way I understood strength. It taught me that resilience is not loud. It is quiet, steady, and deeply internal. Long before I worked with others, I was learning how to sit with myself—through uncertainty, through grief, through the slow work of becoming emotionally and intellectually independent.

Tarot found me in that season of searching. Not as a mystical escape, but as a grounded practice of reflection. A way to make sense of inner chaos. A way to listen when life felt too loud. Over years of personal work, it became clear that what brought me peace could also become a way to serve.`,
  
  purpose: `Today, The Soul Script exists as a sanctuary for seekers who carry their own histories, questions, and quiet battles. It is a space for reflective practice—where inquiry replaces instruction, depth replaces drama, and conscious choice replaces impulse.

At the heart of this work is a simple intention: to help people build emotional resilience, practise self-acceptance, and find the courage to meet themselves with honesty. To nurture curiosity where there has been fear. To replace self-judgment with understanding. And to walk the path of self-discovery not as a performance, but as a lifelong, deeply human practice.`,
  
  closing: `This work is not about prediction or performance.
It is about offering the tools gathered on one path—so others may walk theirs with greater steadiness, clarity, and compassion.`,
};
